import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import webhookSubmissionService from '../../../services/webhookSubmissionService';


const HeroSection = ({ onCtaClick }) => {
  const [spotsRemaining, setSpotsRemaining] = useState(235);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    companyName: ''
  });
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = async (e) => {
    e?.preventDefault();
    
    // Validate required fields
    if (!formData?.fullName || !formData?.email || !formData?.phone || !formData?.companyName) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Submit to webhook table instead of contractor_applications
      const { data, error } = await webhookSubmissionService?.submitHeroForm(formData);

      if (error) {
        setSubmitError(error?.message || 'Failed to submit form. Please try again.');
        return;
      }

      // Clear form and show success
      setFormData({
        fullName: '',
        phone: '',
        email: '',
        companyName: ''
      });
      
      setSubmitSuccess(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);

    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative bg-white min-h-screen">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
                <Icon name="Home" size={24} color="white" strokeWidth={2.5} />
              </div>
              <div className="flex flex-col">
                <div className="text-2xl font-bold text-gray-900">
                  Pro<span className="text-orange-500">Connect</span>
                </div>
                <div className="text-sm text-gray-500 -mt-1">Emergency home services</div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#benefits" className="text-gray-700 hover:text-gray-900 font-medium">Benefits</a>
              <a href="#earnings" className="text-gray-700 hover:text-gray-900 font-medium">Earnings</a>
              <a href="#early-access" className="text-gray-700 hover:text-gray-900 font-medium">Early Access</a>
            </nav>

            {/* CTA Button */}
            <Button
              variant="default"
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3"
              onClick={() => document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Join Network
            </Button>
          </div>
        </div>
      </header>

      {/* Orange Notification Banner */}
      <div className="bg-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="text-center font-semibold">
            Founding Contractor Applications - Only <span className="font-bold">{spotsRemaining}</span> Spots Remaining
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Column - Content */}
          <div className="text-center lg:text-left">
            {/* Main Headline */}
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Make{' '}
              <span className="text-orange-500">$13,000+</span>{' '}
              More Per Year
            </h1>

            {/* Blue Subheading */}
            <h2 className="text-3xl lg:text-4xl font-bold text-blue-600 mb-4">
              Without Marketing
            </h2>

            {/* Grey Text */}
            <p className="text-2xl lg:text-3xl text-gray-500 font-medium mb-8">
              Or Chasing Payments
            </p>

            {/* Description */}
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto lg:mx-0">
              Join Tampa Bay's Best Home Service Marketplace
            </p>

            {/* Stats */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <div className="text-3xl font-bold text-gray-900 mb-2">$126,542/year</div>
              <div className="text-gray-600">
                Zero marketing costs. Payment Protection. Premium customers only.
              </div>
            </div>

            {/* Service Areas */}
            <div className="text-sm text-gray-600 space-y-1">
              <div className="font-semibold">Licensed & insured contractors only</div>
              <div>HVAC, Plumbing, Electrical</div>
              <div>Tampa Bay area</div>
            </div>
          </div>

          {/* Right Column - Application Form */}
          <div className="lg:mt-8">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" id="application-form">
              {/* Form Header */}
              <div className="bg-white px-8 py-6 border-b border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">
                  Apply for Founding Contractor Status
                </h3>
                <p className="text-sm text-gray-600 text-center">
                  Licensed & insured contractors only
                </p>
              </div>

              {/* Form Content */}
              <form onSubmit={handleFormSubmit} className="p-8 space-y-6">
                {/* Error Display */}
                {submitError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {submitError}
                  </div>
                )}

                {/* Success Display */}
                {submitSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                    Thank you! Your information has been submitted. We'll contact you within 24 hours.
                  </div>
                )}

                <Input
                  label="Full Name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData?.fullName}
                  onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                  required
                  className="w-full"
                  disabled={isSubmitting}
                />

                <Input
                  label="Phone Number"
                  type="tel"
                  placeholder="(813) 555-0123"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  required
                  className="w-full"
                  disabled={isSubmitting}
                />

                <Input
                  label="Email Address"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  required
                  className="w-full"
                  disabled={isSubmitting}
                />

                <Input
                  label="Company Name"
                  type="text"
                  placeholder="Your Company LLC"
                  value={formData?.companyName}
                  onChange={(e) => handleInputChange('companyName', e?.target?.value)}
                  required
                  className="w-full"
                  disabled={isSubmitting}
                />

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Apply Now'}
                </Button>

                {/* Trust Indicators */}
                <div className="text-center text-sm text-gray-500 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-center space-x-6">
                    <div className="flex items-center">
                      <Icon name="Shield" size={16} className="mr-1 text-green-500" />
                      <span>Secure</span>
                    </div>
                    <div className="flex items-center">
                      <Icon name="Clock" size={16} className="mr-1 text-blue-500" />
                      <span>24h Response</span>
                    </div>
                    <div className="flex items-center">
                      <Icon name="CheckCircle" size={16} className="mr-1 text-orange-500" />
                      <span>No Commitment</span>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;